// Task Template List
// Display list of task templates with actions

import React from 'react';
import { Edit, Copy, Trash2, Play } from 'lucide-react';
import { useTaskTemplateMutations } from '../../../hooks/useTaskTemplates';
import { useTaskTypes } from '../../../hooks/useTaskNomenclatures';
import './TaskTemplateList.css';

const TaskTemplateList = ({ templates, onEdit, onRefresh, userId }) => {
  const { deleteTemplate, duplicateTemplate } = useTaskTemplateMutations();
  const { items: taskTypes } = useTaskTypes();

  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete the template "${templateName}"?`)) {
      return;
    }

    try {
      await deleteTemplate(templateId, userId);
      onRefresh();
    } catch (error) {
      alert(`Error deleting template: ${error.message}`);
    }
  };

  const handleDuplicate = async (templateId) => {
    try {
      await duplicateTemplate(templateId, userId);
      onRefresh();
    } catch (error) {
      alert(`Error duplicating template: ${error.message}`);
    }
  };

  // Helper function to get task type name
  const getTaskTypeName = (taskTypeId) => {
    if (!taskTypeId || !taskTypes) return 'Not set';
    const taskType = taskTypes.find(type => type.id === taskTypeId);
    return taskType ? taskType.name : 'Unknown';
  };

  // Helper function to format protocols list
  const formatProtocols = (protocols) => {
    if (!protocols || protocols.length === 0) return 'None';
    return protocols.map((p, index) => `${index + 1}. ${p.protocolName}`).join(', ');
  };

  if (templates.length === 0) {
    return (
      <div className="empty-state">
        <p>No task templates found</p>
        <p className="empty-hint">Create your first template to get started</p>
      </div>
    );
  }

  return (
    <div className="template-list-table">
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Task Type</th>
            <th>Protocols</th>
            <th>Duration</th>
            <th>Priority</th>
            <th>Usage</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map(template => (
            <tr key={template.id}>
              <td>
                <div className="name-cell">
                  <strong>{template.name}</strong>
                </div>
              </td>
              <td>
                <div className="description-cell">
                  {template.description || 'No description'}
                </div>
              </td>
              <td>
                <span className="task-type-badge">
                  {getTaskTypeName(template.taskType)}
                </span>
              </td>
              <td>
                <div className="protocols-cell" title={formatProtocols(template.protocols)}>
                  {template.protocols?.length || 0} protocol(s)
                  {template.protocols && template.protocols.length > 0 && (
                    <div className="protocols-list">
                      {template.protocols.map((protocol, index) => (
                        <div key={index} className="protocol-item">
                          {index + 1}. {protocol.protocolName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </td>
              <td>
                <span className="duration-badge">
                  {template.timingRules?.estimatedDuration} {template.timingRules?.durationUnit}
                </span>
              </td>
              <td>
                <span className={`priority-badge priority-${template.defaultPriority}`}>
                  {template.defaultPriority}
                </span>
              </td>
              <td>
                <span className="usage-badge">
                  {template.usageCount || 0} times
                </span>
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    className="btn btn-edit btn-icon"
                    onClick={() => onEdit(template)}
                    title="Edit Template"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    className="btn btn-secondary btn-icon"
                    onClick={() => handleDuplicate(template.id)}
                    title="Duplicate Template"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    className="btn btn-delete btn-icon"
                    onClick={() => handleDelete(template.id, template.name)}
                    title="Delete Template"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TaskTemplateList;

